import type { Ref } from 'vue'

import { UnsupportedActionError } from '../../lib/boardInvariants'
import {
  PlayerClearAltDocument,
  PlayerCycleRoleDocument,
  PlayerRenameDocument,
  PlayerSetMainWeaponDocument,
} from '@/shared/api/gql/graphql'
import type { Player, PlayerGroup } from '../../types'

export interface WeaponActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly groups: Ref<ReadonlyArray<PlayerGroup>>
  readonly run: <T>(
    mutation: unknown,
    variables: Record<string, unknown>,
    done?: (result: T) => void,
  ) => Promise<void>
}

export interface WeaponActionsApi {
  readonly cycleRole: (id: string) => void
  readonly renamePlayer: (
    id: string,
    field: 'discordName' | 'inGameName',
    value: string,
  ) => void
  readonly setMainWeapon: (id: string, slotIndex: number, slug: string) => void
  readonly setAltWeapon: (id: string, slotIndex: number, slug: string) => Promise<void>
  readonly addMainSlot: (id: string, slug: string) => void
  readonly addAltPair: (id: string, slug: string) => Promise<void>
  readonly clearAlt: (id: string) => void
}

export function useWeaponActions(ctx: WeaponActionsCtx): WeaponActionsApi {
  function cycleRole(id: string): void {
    void ctx.run(PlayerCycleRoleDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
    })
  }

  function renamePlayer(
    id: string,
    field: 'discordName' | 'inGameName',
    value: string,
  ): void {
    void ctx.run(PlayerRenameDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      field,
      value,
    })
  }

  function setMainWeapon(id: string, slotIndex: number, slug: string): void {
    void ctx.run(PlayerSetMainWeaponDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
      slot: slotIndex,
      slug,
    })
  }

  function setAltWeapon(_id: string, _slotIndex: number, _slug: string): Promise<void> {
    const err = new UnsupportedActionError('setAltWeapon')
    ctx.error.value = err.message
    return Promise.reject(err)
  }

  function addMainSlot(id: string, slug: string): void {
    const p = ctx.players.value.find((x) => x.id === id)
    const slot = p ? p.mainWeapons.length : 0
    setMainWeapon(id, slot, slug)
  }

  function addAltPair(_id: string, _slug: string): Promise<void> {
    const err = new UnsupportedActionError('addAltPair')
    ctx.error.value = err.message
    return Promise.reject(err)
  }

  function clearAlt(id: string): void {
    void ctx.run(PlayerClearAltDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(id),
    })
  }

  return {
    cycleRole,
    renamePlayer,
    setMainWeapon,
    setAltWeapon,
    addMainSlot,
    addAltPair,
    clearAlt,
  }
}
